<script setup lang="ts">
  import type { SelectOption, SpecialisationGroup } from '~/types/apply'
  import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/vue'
  import { useField } from 'vee-validate'

  type OptionValue = SelectOption['value']

  // Single hierarchical multi-select for sectors + sub-sectors: the dropdown lists each sector in
  // bold with its sub-sectors indented beneath. Styled to match FormField's combobox. Each sector
  // and sub-sector is an independent value (`sector:<id>` / `subSector:<id>`) — selecting one has
  // no effect on the others; they're split back into the two relations at submit (see step-profile.vue).
  const props = withDefaults(
    defineProps<{
      name: string
      groups?: SpecialisationGroup[]
      value?: OptionValue[]
      placeholder?: string
      error?: string
      /** Show the multi-value hint only where explicitly requested. */
      showAddHint?: boolean
    }>(),
    { groups: () => [], value: () => [], placeholder: 'Domaines et sous-domaines', error: '', showAddHint: false }
  )
  const emit = defineEmits<{
    select: [values: OptionValue[]]
  }>()

  const { value: fieldValue } = useField<OptionValue[]>(() => props.name)
  const query = ref('')
  // Do not display the hint while the input cursor is visible.
  const focused = ref(false)
  const selected = ref<OptionValue[]>(Array.isArray(props.value) ? [...props.value] : [])
  const syncingFromProps = ref(false)
  const sameValues = (a: OptionValue[] = [], b: OptionValue[] = []) => a.length === b.length && a.every((value, index) => value === b[index])

  watch(
    () => props.value,
    v => {
      const next = Array.isArray(v) ? [...v] : []
      if (sameValues(next, selected.value)) return
      syncingFromProps.value = true
      selected.value = next
    },
    { immediate: true }
  )
  watch(selected, v => {
    fieldValue.value = v
    if (syncingFromProps.value) {
      syncingFromProps.value = false
      return
    }
    emit('select', v)
    query.value = ''
  })

  const allOptions = computed(() => props.groups.flatMap(g => [g.sector, ...g.subSectors]))
  const labelOf = (val: OptionValue) => allOptions.value.find(o => o.value === val)?.label ?? val

  // A sector match keeps it + all its sub-sectors; otherwise keep only the matching sub-sectors.
  const filteredGroups = computed(() => {
    const q = query.value.trim().toLowerCase()
    if (!q) return props.groups
    return props.groups
      .map(g => {
        if (g.sector.label.toLowerCase().includes(q)) return g
        const subSectors = g.subSectors.filter(s => s.label.toLowerCase().includes(q))
        return subSectors.length ? { sector: g.sector, subSectors } : null
      })
      .filter((g): g is SpecialisationGroup => !!g)
  })

  const remove = (val: OptionValue) => (selected.value = selected.value.filter(v => v !== val))

  const theme = useCareerTheme()
  const valueTagStyle = computed(() => (theme.value.primary ? { backgroundColor: theme.value.primary } : undefined))
  const valueTagClass = computed(() => [
    'inline-flex max-w-full cursor-pointer items-center space-x-1 rounded-sm px-1 py-0.5 text-sm duration-300',
    theme.value.primary ? 'text-white hover:brightness-95' : 'bg-viking-400 text-tarawera-900 hover:bg-viking-500'
  ])
</script>

<template>
  <div>
    <Combobox class="relative" v-model="selected" multiple as="div">
      <div class="relative w-full">
        <div class="group form-select flex min-h-[1.5rem] w-full min-w-0 flex-wrap items-center gap-1 rounded-md pr-10" :class="{ error: !!error }">
          <div class="relative z-[9] flex min-w-0 flex-wrap gap-1 overflow-hidden" v-if="selected.length">
            <div v-for="val in selected" :key="val" :class="valueTagClass" :style="valueTagStyle" @click.stop="remove(val)">
              <span class="truncate">{{ labelOf(val) }}</span>
              <Icon class="flex-shrink-0 text-white" name="xmark" size="sm" />
            </div>
          </div>
          <FormAddHint v-if="showAddHint && selected.length && !focused" />
          <ComboboxInput
            class="form-input min-w-0 flex-1 border-none bg-transparent p-0 focus:ring-0"
            :display-value="() => query"
            :placeholder="selected.length ? '' : placeholder"
            @change="query = $event.target.value"
            @focus="focused = true"
            @blur="focused = false"
          />
        </div>
        <ComboboxButton class="absolute inset-y-0 right-0 z-0 flex w-full items-center text-zinc-500" as="button" />
      </div>

      <ComboboxOptions
        class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow outline-none"
      >
        <p class="px-2 py-1.5 text-sm text-zinc-400" v-if="!filteredGroups.length">Aucun résultat</p>
        <template v-for="group in filteredGroups" :key="group.sector.value">
          <!-- Sector (bold) -->
          <ComboboxOption v-slot="{ active, selected: isSelected }" :value="group.sector.value" as="template">
            <li
              :class="[
                'flex cursor-pointer items-center justify-between px-2 py-1 font-bold',
                isSelected ? 'bg-zinc-200' : active ? 'bg-zinc-100' : ''
              ]"
            >
              <span>{{ group.sector.label }}</span>
              <Icon
                class="flex-shrink-0"
                v-if="isSelected"
                :class="theme.primary ? '' : 'text-viking-500'"
                :style="theme.primary ? { color: theme.primary } : undefined"
                name="check"
                size="sm"
              />
            </li>
          </ComboboxOption>
          <!-- Its sub-sectors (indented) -->
          <ComboboxOption v-for="sub in group.subSectors" v-slot="{ active, selected: isSelected }" :key="sub.value" :value="sub.value" as="template">
            <li
              :class="[
                'flex cursor-pointer items-center justify-between py-1 pl-6 pr-2 text-sm',
                isSelected ? 'bg-zinc-200' : active ? 'bg-zinc-100' : ''
              ]"
            >
              <span>{{ sub.label }}</span>
              <Icon
                class="flex-shrink-0"
                v-if="isSelected"
                :class="theme.primary ? '' : 'text-viking-500'"
                :style="theme.primary ? { color: theme.primary } : undefined"
                name="check"
                size="sm"
              />
            </li>
          </ComboboxOption>
        </template>
      </ComboboxOptions>
    </Combobox>
    <p class="mt-1 text-sm text-red-600" v-if="error">{{ error }}</p>
  </div>
</template>
